import { useEffect, useRef, useState } from 'react';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Building2 } from 'lucide-react';
import type { StudioId } from '@/domain/types';
import clsx from 'clsx';

export default function StudioSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const persona = useSessionStore((s) => s.persona);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const activeStudio = useSessionStore((s) => s.activeStudio);
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);
  const studios = useBrainStore((s) => s.studios);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', outside);
    window.addEventListener('pointerdown', outside);
    window.addEventListener('touchstart', outside);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', outside);
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('touchstart', outside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!persona || assignedStudios.length < 2) return null;

  const active = activeStudio ? studios[activeStudio] : undefined;

  const pickStudio = (id: StudioId) => {
    setActiveStudio(id);
    setOpen(false);
    // If currently on a studio-scoped route, swap to the new studio.
    if (location.pathname.startsWith('/studio/')) navigate(`/studio/${id}`);
    else if (location.pathname.startsWith('/calendar/')) navigate(`/calendar/${id}`);
  };

  return (
    <div ref={ref} className="relative" data-testid="studio-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-dim/40 hover:border-gold-bright/70 text-cream-soft hover:text-cream transition text-xs"
      >
        <Building2 size={12} />
        <span className="font-inter uppercase tracking-widest">
          {active ? active.name : 'Pick studio'}
        </span>
        <ChevronDown size={12} />
      </button>
      {open ? (
        <div className="absolute right-0 mt-2 w-72 z-50 panel py-2 shadow-ink-soft">
          {assignedStudios.map((id) => {
            const s = studios[id];
            if (!s) return null;
            const selected = id === activeStudio;
            return (
              <button
                key={id}
                type="button"
                data-testid={`studio-switch-${id}`}
                onClick={() => pickStudio(id)}
                className={clsx(
                  'w-full px-3 py-2 text-left flex items-center justify-between gap-3 hover:bg-ink-soft/50',
                  selected ? 'bg-gold/10' : '',
                )}
              >
                <span>
                  <span
                    className={clsx(
                      'block font-inter text-[9px] uppercase tracking-widest',
                      selected ? 'text-gold-bright' : 'text-gold',
                    )}
                  >
                    {s.brand}
                  </span>
                  <span className="font-inter text-sm text-cream">{s.name}</span>
                </span>
                <span className="font-mono text-[10px] text-cream-soft">{s.state}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
